import React from 'react';
import styled from 'styled-components';
import { color, screen } from 'theme';

interface Fredcon26AgendaItemProps {
  startTime?: string;
  endTime?: string;
  title: string;
  subtitle?: string;
  speaker?: string;
  isBreak?: boolean;
}

const Wrapper = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: center;
  padding: 16px 24px;
  width: 100%;

  @media (max-width: ${screen('md2')}) {
    flex-direction: column;
    gap: 8px;
    padding: 16px 0;
  }
`;

const TimeContainer = styled.div`
  display: flex;
  gap: 12px;
  padding-top: 4px;
  align-items: center;
  justify-content: flex-end;
  width: 200px;
  flex-shrink: 0;

  @media (max-width: ${screen('md2')}) {
    width: auto;
    justify-content: flex-start;
    align-items: flex-end;
    gap: 8px;
  }
`;

const TimeBox = styled.div<{ isBreak?: boolean }>`
  background-color: ${({ isBreak }) => (isBreak ? '#C5FE79' : '#3053F9')};
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;

  p {
    font-family: 'Geist Mono', monospace;
    font-size: 20px;
    color: ${({ isBreak }) => (isBreak ? color('black') : color('white'))};
    line-height: 1;
    margin: 0;
    white-space: nowrap;

    @media (max-width: ${screen('md2')}) {
      font-size: 16px;
      line-height: 16px;
    }
  }
`;

const TimeSeparator = styled.p`
  font-family: 'Geist Mono', monospace;
  font-size: 24px;
  color: ${color('black')};
  margin: 0;
  line-height: 24px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;

  @media (max-width: ${screen('md2')}) {
    width: 100%;
    gap: 8px;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-family: 'Archivo Narrow', sans-serif;
  font-weight: 700;
  color: ${color('black')};
`;

const Title = styled.p`
  font-size: 24px;
  line-height: 32px;
  margin: 0;

  @media (max-width: ${screen('md2')}) {
    font-size: 24px;
    line-height: 30px;
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  line-height: 24px;
  margin: 0;
  opacity: 0.8;

  @media (max-width: ${screen('md2')}) {
    font-size: 19px;
    line-height: 22px;
  }
`;

const Speaker = styled.p`
  font-family: 'Archivo Narrow', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: ${color('black')};
  margin: 0;

  @media (max-width: ${screen('md2')}) {
    font-size: 19px;
    line-height: 24px;
  }
`;

export const Fredcon26AgendaItem: React.FC<Fredcon26AgendaItemProps> = ({
  startTime,
  endTime,
  title,
  subtitle,
  speaker,
  isBreak
}) => {
  const isTBD = title === 'TBD' || !title;
  const finalTitle = isTBD && speaker ? speaker : title;

  // We hide subtitles and speaker labels if we are using the speaker as title,
  // or if they are explicitly requested to be hidden for now.
  const showExtraInfo = !isBreak && !isTBD && speaker !== finalTitle;

  const hasStart = !!startTime;
  const hasEnd = !!endTime;

  return (
    <Wrapper>
      <TimeContainer>
        {hasStart && (
          <TimeBox isBreak={isBreak}>
            <p>{startTime}</p>
          </TimeBox>
        )}
        {hasStart && hasEnd && <TimeSeparator>-</TimeSeparator>}
        {hasEnd && (
          <TimeBox isBreak={isBreak}>
            <p>{endTime}</p>
          </TimeBox>
        )}
      </TimeContainer>

      <Content>
        <TitleWrapper>
          {finalTitle && <Title>{finalTitle}</Title>}
          {subtitle && showExtraInfo && <Subtitle>{subtitle}</Subtitle>}
        </TitleWrapper>
        {speaker && showExtraInfo && <Speaker>{speaker}</Speaker>}
      </Content>
    </Wrapper>
  );
};
